import React from 'react';
import { Box, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';

type ResendBadgeProps = Readonly<{
  /**
   * Which attempt this row is, counting the first send as 1 — so always 2 or more here, since the
   * badge only sits on resends. Counted from the rows on screen, so it is a floor rather than a
   * lifetime total; the tooltip says so rather than letting the number read as authoritative.
   *
   * Required: the number IS the label, so there is no badge to render without one.
   */
  attemptNumber: number;
}>;

/**
 * Marks a row as a later attempt rather than an original send.
 *
 * Without it a resend row is indistinguishable from a duplicate message, which is the CX confusion
 * this feature exists to remove. The table shows only the latest attempt, so this badge is also
 * what says the earlier ones exist — expand the row to see the original.
 *
 * The number carries that on its own: anything above 1 is by definition not the first send, so the
 * badge does not also spell out "resend".
 */
export const ResendBadge: React.FC<ResendBadgeProps> = ({ attemptNumber }) => {
  const { tq } = useTranslation('messagesV2ServiceCenter');

  const badge = (
    <Box
      // Focusable so the tooltip is reachable without a mouse: it is the only place that says the
      // number counts visible results only, and a div gets no focus of its own.
      tabIndex={0}
      display="inline-block"
      paddingX="6px"
      paddingY="1px"
      borderRadius="100px"
      background="rgb(238,235,255)"
      color="#4951EF"
      fontSize="9.5px"
      fontWeight={700}
      letterSpacing="0.03em"
      whiteSpace="nowrap"
      cursor="help"
    >
      {tq`ATTEMPT ${String(attemptNumber)}`}
    </Box>
  );

  return (
    <TooltipV2
      content={
        <TextV2 typographyVariant="label3">
          {tq`Attempt ${String(attemptNumber)}, counted from these results and including the first send. Earlier attempts on another page are not counted. Only the latest is listed — expand this row to see the original.`}
        </TextV2>
      }
    >
      {badge}
    </TooltipV2>
  );
};
